import React, { useState, useEffect } from "react";
import { countries } from "countries-list";
import MetaData from "../../components/layout/MetaData";
import { useNavigate } from "react-router-dom";

// Redux
import { useDispatch, useSelector } from "react-redux";

import { saveShippingInfo } from "../../redux/features/cartSlice";

import CheckoutSteps from "../../components/CheckoutSteps";

const CUSTOMER_TYPES = ["Individual", "Business", "Education", "Government"];
const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NM",
  "NV",
  "NY",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
];

/**
 * 按 DigiKey 地址页风格渲染字段标签。
 */
const FieldLabel = ({ htmlFor, children, required = false }) => (
  <label
    htmlFor={htmlFor}
    className="mb-2 block text-[16px] font-bold uppercase leading-tight text-[#06051f]"
  >
    {children} {required && <span>*</span>}
  </label>
);

/**
 * 生成统一的输入框样式，保持订单表单的朴素边框风格。
 */
const getFieldClassName = (extraClassName = "") =>
  `h-[38px] border border-[#b7becb] bg-white px-3 text-[18px] text-[#06051f] outline-none transition focus:border-[#1b3c7a] focus:ring-1 focus:ring-[#1b3c7a] ${extraClassName}`;

/**
 * 将旧邮编拆为 ZIP+4 两段，兼容历史 localStorage 数据。
 */
const splitPostalCode = (zipCode = "") => {
  const [postalCode = "", postalCodeExtra = ""] = String(zipCode).split("-");
  return {
    postalCode,
    postalCodeExtra,
  };
};

/**
 * 将两段邮编合并为后续订单流程仍然读取的 zipCode 字段。
 */
const buildZipCode = (postalCode, postalCodeExtra) => {
  const baseCode = String(postalCode || "").trim();
  const extraCode = String(postalCodeExtra || "").trim();
  return extraCode ? `${baseCode}-${extraCode}` : baseCode;
};

/**
 * 结账流程收货地址页面，负责收集并保存用户配送信息。
 */
const Shipping = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { shippingInfo } = useSelector((state) => state.cart);
  const { user } = useSelector((state) => state.auth);
  const countriesList = Object.values(countries);

  const [email, setEmail] = useState("");
  const [telephone, setTelephone] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [country, setCountry] = useState("United States");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [mailStop, setMailStop] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [postalCodeExtra, setPostalCodeExtra] = useState("");

  useEffect(() => {
    if (shippingInfo) {
      const savedPostalCode = splitPostalCode(
        shippingInfo?.postalCode || shippingInfo?.zipCode
      );
      setEmail(shippingInfo?.email || user?.email || "");
      setTelephone(shippingInfo?.telephone || shippingInfo?.phoneNo || "");
      setCustomerType(shippingInfo?.customerType || "");
      setCountry(shippingInfo?.country || "United States");
      setFirstName(shippingInfo?.firstName || "");
      setLastName(shippingInfo?.lastName || "");
      setCompanyName(shippingInfo?.companyName || "");
      setMailStop(shippingInfo?.mailStop || "");
      setAddressLine1(shippingInfo?.addressLine1 || shippingInfo?.address || "");
      setAddressLine2(shippingInfo?.addressLine2 || "");
      setCity(shippingInfo?.city || "");
      setStateProvince(shippingInfo?.stateProvince || "");
      setPostalCode(savedPostalCode.postalCode);
      setPostalCodeExtra(savedPostalCode.postalCodeExtra);
    }
  }, [shippingInfo, user?.email]);

  /**
   * 保存收货信息并进入支付方式选择页。
   */
  const submitHandler = (e) => {
    e.preventDefault();

    const zipCode = buildZipCode(postalCode, postalCodeExtra);

    dispatch(
      saveShippingInfo({
        email,
        telephone,
        customerType,
        country,
        firstName,
        lastName,
        companyName,
        mailStop,
        addressLine1,
        addressLine2,
        city,
        stateProvince,
        postalCode,
        postalCodeExtra,
        address: addressLine1,
        phoneNo: telephone,
        zipCode,
      })
    );

    navigate("/payment_method");
  };

  return (
    <>
      <MetaData title="Shipping Info" />
      <CheckoutSteps shipping />
      <div className="bg-[#f1f3f7] px-4 py-6 text-[#06051f]">
        <div className="mx-auto max-w-[1180px]">
          <h1 className="mb-4 text-[18px] font-bold leading-tight text-[#06051f]">
            Shipping Address
          </h1>

          <form onSubmit={submitHandler}>
            <div className="min-h-[920px] border border-[#b7becb] bg-white px-4 py-8 sm:px-6 lg:px-4">
              <p className="mb-5 text-[18px] italic leading-6 text-[#06051f]">
                All fields marked with * are required
              </p>

              <div className="max-w-[690px]">
                <div className="mb-4">
                  <FieldLabel htmlFor="email_field" required>
                    Email
                  </FieldLabel>
                  <input
                    type="email"
                    id="email_field"
                    className={getFieldClassName("w-full")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-4 max-w-[310px]">
                  <FieldLabel htmlFor="telephone_field" required>
                    Telephone
                  </FieldLabel>
                  <input
                    type="tel"
                    id="telephone_field"
                    className={getFieldClassName("w-full")}
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-5 max-w-[242px]">
                  <FieldLabel htmlFor="customer_type_field" required>
                    Customer Type{" "}
                    <span className="text-[#1b16c4]">(Not Sure?)</span>
                  </FieldLabel>
                  <select
                    id="customer_type_field"
                    className={getFieldClassName("w-full")}
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value)}
                    required
                  >
                    <option value=""></option>
                    {CUSTOMER_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4 max-w-[235px]">
                  <FieldLabel htmlFor="country_field" required>
                    Country/Region
                  </FieldLabel>
                  <select
                    id="country_field"
                    className={getFieldClassName("w-full")}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    required
                  >
                    <option value="United States">USA</option>
                    {countriesList
                      .filter((countryItem) => countryItem.name !== "United States")
                      .map((countryItem) => (
                        <option key={countryItem.name} value={countryItem.name}>
                          {countryItem.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="mb-4 grid gap-6 md:grid-cols-2">
                  <div>
                    <FieldLabel htmlFor="first_name_field" required>
                      First Name
                    </FieldLabel>
                    <input
                      type="text"
                      id="first_name_field"
                      className={getFieldClassName("w-full")}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="last_name_field" required>
                      Last Name
                    </FieldLabel>
                    <input
                      type="text"
                      id="last_name_field"
                      className={getFieldClassName("w-full")}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <FieldLabel htmlFor="company_name_field" required>
                    Company Name
                  </FieldLabel>
                  <input
                    type="text"
                    id="company_name_field"
                    className={getFieldClassName("w-full")}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-4">
                  <FieldLabel htmlFor="mail_stop_field">Mail Stop</FieldLabel>
                  <input
                    type="text"
                    id="mail_stop_field"
                    className={getFieldClassName("w-full")}
                    value={mailStop}
                    onChange={(e) => setMailStop(e.target.value)}
                  />
                </div>

                <div className="mb-4">
                  <FieldLabel htmlFor="address_line_1_field" required>
                    Address Line 1
                  </FieldLabel>
                  <input
                    type="text"
                    id="address_line_1_field"
                    className={getFieldClassName("w-full")}
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-4">
                  <FieldLabel htmlFor="address_line_2_field">
                    Address Line 2
                  </FieldLabel>
                  <input
                    type="text"
                    id="address_line_2_field"
                    className={getFieldClassName("w-full")}
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                  />
                </div>

                <div className="mb-4 grid gap-6 md:grid-cols-[minmax(0,1fr)_140px]">
                  <div>
                    <FieldLabel htmlFor="city_field" required>
                      City
                    </FieldLabel>
                    <input
                      type="text"
                      id="city_field"
                      className={getFieldClassName("w-full")}
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="state_field" required>
                      State
                    </FieldLabel>
                    <select
                      id="state_field"
                      className={getFieldClassName("w-full")}
                      value={stateProvince}
                      onChange={(e) => setStateProvince(e.target.value)}
                      required
                    >
                      <option value="">Select</option>
                      {US_STATES.map((stateCode) => (
                        <option key={stateCode} value={stateCode}>
                          {stateCode}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <FieldLabel htmlFor="postal_code_field" required>
                    Postal Code
                  </FieldLabel>
                  <div className="flex items-center gap-1">
                    <input
                      type="text"
                      id="postal_code_field"
                      className={getFieldClassName("w-[102px]")}
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      required
                    />
                    <span className="text-[18px] text-[#06051f]">-</span>
                    <input
                      type="text"
                      aria-label="Postal code extension"
                      className={getFieldClassName("w-[78px]")}
                      value={postalCodeExtra}
                      onChange={(e) => setPostalCodeExtra(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end border-x border-b border-[#b7becb] bg-white px-4 py-5 sm:px-6 lg:px-4">
              <button
                type="submit"
                className="border border-[#081c45] bg-[#081c45] px-7 py-3 text-[16px] font-bold uppercase text-white transition hover:bg-[#102f68] focus:outline-none focus:ring-2 focus:ring-[#1b3c7a] focus:ring-offset-2"
              >
                Continue to Payment
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Shipping;
